import { useCallback, useState } from 'react';

import {
  divideMoneyValues,
  isPositiveMoneyValue,
  limitMoneyDecimals,
  sanitizeMoneyValue,
} from '@/shared/design-system';

/** Both unit figures are recorded to the cent, matching the money they settle. */
export const UNIT_DECIMALS = 2;

/** Which figure the admin stated, and so which one the other is computed from. */
type Stated = 'units' | 'unitPrice' | null;

export type UnitAllocation = {
  received: string;
  units: string;
  unitPrice: string;
  /** True once the three amounts are a set the backend will accept. */
  isComplete: boolean;
  setReceived: (_value: string) => void;
  setUnits: (_value: string) => void;
  setUnitPrice: (_value: string) => void;
  reset: (_receivedAmount: string) => void;
};

/**
 * Holds the received/units/unit-price triangle for the confirm dialogs, where
 * `received = units × unitPrice` is the only arrangement the ledger can record.
 *
 * The admin states either figure and the hook computes the other from the amount
 * that arrived, so an inconsistent pair is not reachable by typing. Whichever
 * figure was stated last stays as typed and the other is recomputed — including
 * when the received amount itself is edited, which re-prices rather than
 * invalidating what the admin already said.
 *
 * Everything is a decimal string, never a number, for the same reason the rest of
 * this flow is: what the admin typed is what the ledger records. The one place a
 * value is not exact is the computed figure, which is rounded to
 * `UNIT_DECIMALS`; a stated price of 3 against $10,000 records 3,333.33 units,
 * an implied 3.000003 per unit. Only the units cross the wire, so that cent of
 * rounding lands where the fund can see it rather than inside a price nobody
 * stored.
 */
export const useUnitAllocation = (initialReceived: string): UnitAllocation => {
  const [received, setReceivedValue] = useState(() => sanitizeMoneyValue(initialReceived));
  const [units, setUnitsValue] = useState('');
  const [unitPrice, setUnitPriceValue] = useState('');
  const [stated, setStated] = useState<Stated>(null);

  const setUnits = useCallback(
    (value: string) => {
      const typed = limitMoneyDecimals(value, UNIT_DECIMALS);
      setUnitsValue(typed);
      setUnitPriceValue(divideMoneyValues(received, typed, UNIT_DECIMALS) ?? '');
      setStated('units');
    },
    [received],
  );

  const setUnitPrice = useCallback(
    (value: string) => {
      const typed = limitMoneyDecimals(value, UNIT_DECIMALS);
      setUnitPriceValue(typed);
      setUnitsValue(divideMoneyValues(received, typed, UNIT_DECIMALS) ?? '');
      setStated('unitPrice');
    },
    [received],
  );

  const setReceived = useCallback(
    (value: string) => {
      setReceivedValue(value);
      if (stated === 'units') {
        setUnitPriceValue(divideMoneyValues(value, units, UNIT_DECIMALS) ?? '');
      } else if (stated === 'unitPrice') {
        setUnitsValue(divideMoneyValues(value, unitPrice, UNIT_DECIMALS) ?? '');
      }
    },
    [stated, units, unitPrice],
  );

  const reset = useCallback((receivedAmount: string) => {
    setReceivedValue(sanitizeMoneyValue(receivedAmount));
    setUnitsValue('');
    setUnitPriceValue('');
    setStated(null);
  }, []);

  return {
    received,
    units,
    unitPrice,
    isComplete:
      isPositiveMoneyValue(received) &&
      isPositiveMoneyValue(units) &&
      isPositiveMoneyValue(unitPrice),
    setReceived,
    setUnits,
    setUnitPrice,
    reset,
  };
};
